import React from "react";
import "../styles/A11yResultsPanel.css";
import type { NodeResult } from "axe-core";
import type { A11yScanResult } from "../types";
import { getScoreColor } from "../impactConfig";
import { ViolationItem } from "./ViolationItem";

export interface A11yResultsPanelProps {
  scanResult: A11yScanResult | null;
  isScanning: boolean;
  scanError: string | null;
  onRescan: () => void;
  onClose: () => void;
  /** Called when the user clicks "jump to element" on a specific node row. */
  onNodeClick?: (target: (string | string[])[], node?: NodeResult) => void;
}

export const A11yResultsPanel: React.FC<A11yResultsPanelProps> = ({
  scanResult,
  isScanning,
  scanError,
  onRescan,
  onClose,
  onNodeClick
}) => {
  const violations = scanResult?.violations ?? [];
  const incomplete = scanResult?.incomplete ?? [];
  const passCount = scanResult?.passCount ?? 0;
  const score = scanResult?.score ?? 0;
  const scoreColor = getScoreColor(score);

  return (
    <div
      role="region"
      aria-label="Accessibility scan results"
      aria-live="polite"
      className="a11y-panel"
    >
      {/* Header */}
      <div className="a11y-panel-header">
        <div
          aria-label={`Accessibility score: ${score}%`}
          className="a11y-panel-score-circle"
          style={{ borderColor: isScanning ? "#d1d5db" : scoreColor }}
        >
          {isScanning ? (
            <span className="a11y-panel-scanning-dot">…</span>
          ) : (
            <span
              className="a11y-panel-score-value"
              style={{ color: scoreColor }}
            >
              {score}%
            </span>
          )}
        </div>

        <div className="a11y-panel-meta">
          <div className="a11y-panel-title">A11y Check</div>
          <div className="a11y-panel-subtitle">
            {isScanning ? (
              "Scanning WCAG 2.1 AA…"
            ) : scanError ? (
              <span className="a11y-stat--error">{scanError}</span>
            ) : (
              <>
                WCAG 2.1 AA + best practices ·{" "}
                <span className="a11y-stat--violations">
                  {violations.length} violation
                  {violations.length !== 1 ? "s" : ""}
                </span>
                {incomplete.length > 0 && (
                  <>
                    {" · "}
                    <span className="a11y-stat--review">
                      {incomplete.length} to review
                    </span>
                  </>
                )}
                {" · "}
                <span className="a11y-stat--passed">
                  {passCount} element{passCount !== 1 ? "s" : ""} passed
                </span>
              </>
            )}
          </div>
        </div>

        <div className="a11y-panel-actions">
          <button
            type="button"
            onClick={onRescan}
            disabled={isScanning}
            aria-label="Re-run accessibility scan"
            title="Re-scan"
            className="a11y-panel-action-btn"
          >
            ↺
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close accessibility panel"
            className="a11y-panel-action-btn"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="a11y-panel-body">
        {isScanning && (
          <div className="a11y-panel-state">Running WCAG 2.1 AA checks…</div>
        )}

        {!isScanning && scanError && (
          <div role="alert" className="a11y-panel-error">
            <strong>Scan error:</strong> {scanError}
          </div>
        )}

        {!isScanning &&
          !scanError &&
          violations.length === 0 &&
          incomplete.length === 0 &&
          scanResult && (
            <div className="a11y-panel-state--success">
              ✓ No WCAG 2.1 AA violations found
            </div>
          )}

        {!isScanning && violations.length > 0 && (
          <ul className="a11y-violations-list">
            {violations.map((v) => (
              <ViolationItem
                key={v.id}
                violation={v}
                onNodeClick={onNodeClick}
              />
            ))}
          </ul>
        )}

        {!isScanning && incomplete.length > 0 && (
          <>
            <div
              className={`a11y-panel-section-label${
                violations.length > 0
                  ? " a11y-panel-section-label--bordered"
                  : ""
              }`}
            >
              Needs Review
            </div>
            <ul className="a11y-violations-list">
              {incomplete.map((v) => (
                <ViolationItem
                  key={v.id}
                  violation={v}
                  onNodeClick={onNodeClick}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
